import PersonCard from "@/pages/events/[eventSlug]/_components/PersonCard";
import { Event } from "../../../types";
import { formattedAddress } from "@/lib/addressHelpers";
import { useNavigate } from "react-router-dom";
import SectionHeading from "@/components/SectionHeading";
import { ActivityParticipationRole } from "../../../types";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";
import { EVENT_WITH_ACTIVITY_PARTICIPATIONS_QUERY } from "../../../queries";
import { useQuery } from "@apollo/client";
import { Link } from "@/router";

interface PayoutDetailsProps {
  eventData?: Event;
}

export const getDiffDays = (startDate: string, endDate: string) =>
  DateTime.fromISO(startDate).diff(DateTime.fromISO(endDate), ["days"]).rescale().days + 1;

const getCreditRate = (eventData: Event, role: ActivityParticipationRole) =>
  eventData?.activityType?.config?.creditRates?.find((r: any) => r.participationRole === role);

export const PayoutDetailsSection = ({ eventData }: PayoutDetailsProps) => {
  const navigate = useNavigate();
  const { eventSlug } = useParams();

  const { data: activityParticipationsData } = useQuery<any>(EVENT_WITH_ACTIVITY_PARTICIPATIONS_QUERY, {
    variables: {
      slug: eventSlug,
      hostProfessionalId: eventData?.patEventAttributes?.hostProfessional?.id,
      examinerId: eventData?.patEventAttributes?.examiner?.id
    },
    skip: !eventSlug
  });

  const hostProfessional = eventData?.patEventAttributes?.hostProfessional;
  const examiner = eventData?.patEventAttributes?.examiner;
  const isHostExaminerSamePerson = hostProfessional?.pgaId === examiner?.pgaId;

  const activityTypeParticipations = activityParticipationsData?.event?.activityType;
  const activityTypeId = eventData?.activityType?.id;

  const getParticipationForEvent = (nodes: { id: string; activity?: { event?: { slug?: string } } }[] | undefined) =>
    nodes?.find((p) => p.activity?.event?.slug === eventSlug);

  const getPdrCreditsHeadline = (role: ActivityParticipationRole): React.ReactNode => {
    const durationDays = getDiffDays(eventData?.startsAt || "", eventData?.endsAt || "");

    const finalized = eventData?.patEventAttributes?.finalized ?? false;

    const creditRate = getCreditRate(eventData as Event, role);
    const credits = creditRate ? creditRate.rateMultiplier * durationDays : 0;
    const isHost = role === ActivityParticipationRole.HOST_PROFESSIONAL;
    const participations = isHost
      ? activityTypeParticipations?.hostProfessionalParticipations?.nodes
      : activityTypeParticipations?.examinerParticipations?.nodes;
    const participationForEvent = getParticipationForEvent(participations);
    const text = `${credits} PDR credits ${finalized && participations?.length > 0 ? "awarded" : "pending"}`;

    if (finalized && participationForEvent?.id && activityTypeId) {
      return (
        <Link
          to="/activities/activity-types/:activityTypeId/participants/:participantId"
          params={{ activityTypeId, participantId: participationForEvent.id }}
        >
          {text}
        </Link>
      );
    }
    return text;
  };

  return (
    <>
      <SectionHeading heading="Payout Details" body="Mailing address is required to process payments." />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
        <PersonCard
          title="Event Host"
          name={hostProfessional?.displayName || ""}
          pgaId={hostProfessional?.pgaId || ""}
          pdrCredits={
            hostProfessional?.pdrRequirements && hostProfessional?.pdrRequirements?.requirements?.length > 0
              ? getPdrCreditsHeadline(ActivityParticipationRole.HOST_PROFESSIONAL)
              : ""
          }
          options={[
            {
              overline: "Primary",
              headline: hostProfessional?.contactAddress ? formattedAddress(hostProfessional?.contactAddress) : "",
              iconName: "home",
              ...(hostProfessional?.pgaId && {
                action: () => navigate(`/people/${hostProfessional?.pgaId}/person/contact`)
              })
            }
          ]}
          onSave={() => {}}
          userCanEdit={false}
        />
        <PersonCard
          title="PAT Examiner"
          name={examiner?.displayName || ""}
          pgaId={examiner?.pgaId || ""}
          pdrCredits={
            examiner?.pdrRequirements &&
            examiner?.pdrRequirements?.requirements?.length > 0 &&
            !isHostExaminerSamePerson
              ? getPdrCreditsHeadline(ActivityParticipationRole.EXAMINER)
              : ""
          }
          options={[
            {
              overline: "Primary",
              headline: examiner?.contactAddress ? formattedAddress(examiner?.contactAddress) : "",
              iconName: "home",
              ...(examiner?.pgaId && {
                action: () => navigate(`/people/${examiner?.pgaId}/person/contact`)
              })
            }
          ]}
          userCanEdit={false}
          onSave={() => {}}
        />
      </div>
    </>
  );
};
